import { signal, computed } from '@angular/core';
import { FormGroup, FormArray, FormControl, Validators } from '@angular/forms';

import { plainToClass, plainToInstance } from 'class-transformer';
import { debounceTime, Subject, Subscription } from 'rxjs';

import { BlockType, QuestionType, BlockModel, AddCondition, Condition, Operator, BlockLoopSourceEnum } from '@empathy/survey/base';
import { Question } from '@empathy/survey-core';

import { BlockTypeAllowConditionals, BlockTypeContainers } from './block-type.constants';
import { IEditorForm } from './editor-form.interface';
import { IBlockForm } from '../blocks/block-form.interface';
import { BlockForm } from '../blocks/block-form.model';

import { ConditionForm } from '../conditions/condition-form.model';
import { reserveOptionId } from '../reserve-option-id.function';

export class SurveyBlocks {
  activeIndex$ = signal<number>(-1);
  active$ = computed(() => {
    this.changeListener?.unsubscribe();
    this.quotaSubscriptions.forEach((s) => s.unsubscribe());
    this.quotaSubscriptions = [];

    if (this.activeIndex$() > -1) {
      this.listenToEvenDistribution();
      this.listenToTitleChange();
      this.listenToSubBlocks();
      return this.all.controls[this.activeIndex$()];
    }
  });
  activeAllowsConditionals$ = computed(() => BlockTypeAllowConditionals.includes(this.active$().get('type').value));

  all$ = signal<BlockModel[]>([]);
  change$ = new Subject<{ blocks: BlockModel[]; parentBlock?: BlockModel }>();
  refreshed$ = new Subject<void>();

  private changeListener: Subscription;
  private quotaSubscriptions: Subscription[] = [];

  constructor(private surveyForm: FormGroup<IEditorForm>) {}

  get count(): number {
    return this.all.length;
  }

  get all(): FormArray<FormGroup<IBlockForm>> {
    return this.surveyForm.get('blocks') as FormArray<FormGroup<IBlockForm>>;
  }

  get allLoops(): FormGroup<IBlockForm>[] {
    return (this.surveyForm.get('blocks') as FormArray<FormGroup<IBlockForm>>).controls.filter((b) => b.get('type').value === BlockType.Loop);
  }

  get allGroups(): FormGroup<IBlockForm>[] {
    return (this.surveyForm.get('blocks') as FormArray<FormGroup<IBlockForm>>).controls.filter((b) => b.get('type').value === BlockType.Group);
  }

  private get activeBlockConditions(): FormArray {
    return this.active$().get('visibleWhen') as FormArray;
  }

  private get lastId(): number {
    const blockIds: number[] = this.all.value.filter((b) => !b.isSubBlock).map((block) => +block.id.replace(/^\D+/g, ''));
    return blockIds.length === 0 ? 0 : Math.max.apply(null, blockIds);
  }

  private get questions(): Question[] {
    return plainToInstance(Question, this.surveyForm.get('questions').value);
  }

  bySourceQId(qId: string): BlockModel[] {
    return this.all.value.filter((b) => b.sourceQId === qId) as BlockModel[];
  }

  byGroupedQId(qId: string): BlockModel {
    const blocks = this.all.value;
    const leaf = blocks.find((block) => !BlockTypeContainers.includes(block.type) && block.groupedQuestionIds.includes(qId));
    // Older ordinary parents may hold direct membership without a matching child.
    return (leaf ?? blocks.find((block) => block.type === BlockType.MultiBlock && block.groupedQuestionIds.includes(qId))) as BlockModel;
  }

  byGroupedBlocksId(blockId: string): BlockModel {
    return this.all.value.find((b) => BlockTypeContainers.includes(b.type) && b.groupedBlockIds.includes(blockId)) as BlockModel;
  }

  byId(blockId: string): BlockModel {
    return this.all.value.find((b) => b.id === blockId) as BlockModel;
  }

  getSiblingLastQuestionId(bId: string): string {
    const parentBlock = this.byGroupedBlocksId(bId);
    if (!parentBlock) {
      return null;
    }
    let lastBlockId = null;
    let index = 0;
    let found = false;
    while (!found && index <= parentBlock.groupedBlockIds.length) {
      const tempBlockId = parentBlock.groupedBlockIds[index];
      if (bId !== tempBlockId) {
        lastBlockId = tempBlockId;
      } else {
        found = true;
      }
      index++;
    }

    if (!lastBlockId) {
      return null;
    }

    const lastBlock = this.byId(lastBlockId);
    return lastBlock.groupedQuestionIds[lastBlock.groupedQuestionIds.length - 1];
  }

  parentStartQuestionIndex(childBlockIds: readonly string[], questions: readonly Question[]): number {
    const childQuestionIds = new Set<string>();
    for (const childId of childBlockIds) {
      const child = this.byId(childId);
      for (const questionId of child?.groupedQuestionIds ?? []) {
        childQuestionIds.add(questionId);
      }
    }
    // Child order may be randomized; use the earliest actual question, not the first child ID.
    return questions.findIndex((question) => childQuestionIds.has(question.id));
  }

  allowedSourceQuestions(upToIndex: number = null, currentBlockId: string = null): Question[] {
    const allQuestions = this.questions;
    let questions = upToIndex !== null ? allQuestions.slice(0, upToIndex + 1) : allQuestions;
    const parent = this.byGroupedBlocksId(currentBlockId);
    if (parent?.type === BlockType.LoopParent) {
      const parentStartIndex = this.parentStartQuestionIndex(parent.groupedBlockIds, allQuestions);
      // Child sources must be asked once before the entire parent, regardless of child order.
      // An empty draft parent has no question position yet; allow choosing its first source.
      const questionsBeforeParent = parentStartIndex === -1 ? questions : questions.slice(0, parentStartIndex);
      questions = questionsBeforeParent.filter((question) => !this.isOnLoopBlock(question.id));
    }
    return questions
      .filter((q) => q.type === QuestionType.MultiChoice)
      .filter((q) => !this.all.value.some((b) => (currentBlockId === null || b.id !== currentBlockId) && b.sourceQId === q.id));
  }

  alloweQuestionsOnBlock(startingIndex: number): Question[] {
    return this.questions.slice(startingIndex).filter((q) => !this.all.value.some((b) => b.groupedQuestionIds.includes(q.id)));
  }

  isOnBlock(questionId: string): boolean {
    return this.all.value.some((b) => b.groupedQuestionIds.includes(questionId));
  }

  firstInBlock(questionId: string): BlockModel {
    const block = this.byGroupedQId(questionId);
    if (!block) {
      return;
    }

    if (block?.groupedQuestionIds[0] === questionId) {
      return block;
    }

    return null;
  }

  isOnLoopBlock(questionId: string): boolean {
    return this.allLoops.some((b) => b.value.groupedQuestionIds.includes(questionId));
  }

  push(blockType: BlockType, isSubBlock: boolean = false, blockId: string = null): string {
    const newBlockId = blockId || `B${this.lastId + 1}`;
    const newBlockForm = new BlockForm(newBlockId, blockType, isSubBlock).form;
    this.all.push(newBlockForm);
    this.all$.update((blocks) => [...blocks, plainToClass(BlockModel, newBlockForm.value)]);

    if (!isSubBlock) {
      this.activeIndex$.set(this.count - 1);
    }

    return newBlockId;
  }

  assertQuestionBlock(blockId: string): void {
    const block = this.byId(blockId);
    if (!block) {
      throw new Error(`Question block ${blockId} does not exist`);
    }
    const legacyParent = block.type === BlockType.MultiBlock && block.groupedQuestionIds.some((id) => this.byGroupedQId(id)?.id === block.id);
    if (BlockTypeContainers.includes(block.type) && !legacyParent) {
      throw new Error('Questions must belong to a child block, not a parent');
    }
  }

  addQuestionToBlock(blockId: string, questionId: string, emitEvent: boolean = true): void {
    const block = this.all.controls.find((c) => c.value.id === blockId);
    this.assertQuestionBlock(blockId);
    (block.get('groupedQuestionIds') as FormArray).push(new FormControl(questionId));
    if (emitEvent) {
      this.refreshLists();
      this.emitBlockChange(block.value as BlockModel);
    }
  }

  insertQuestionToBlock(blockId: string, questionId: string, targetQuestionId: string, emitEvent: boolean = true): void {
    const block = this.all.controls.find((c) => c.value.id === blockId);
    this.assertQuestionBlock(blockId);
    const targetIndex = block.value.groupedQuestionIds.findIndex((qId) => qId === targetQuestionId);

    (block.get('groupedQuestionIds') as FormArray).insert(targetIndex + 1, new FormControl(questionId));

    if (emitEvent) {
      this.refreshLists();
      this.emitBlockChange(block.value as BlockModel);
    }
  }

  removeActive(): void {
    if (BlockTypeContainers.includes(this.active$().value.type)) {
      this.active$()
        .get('groupedBlockIds')
        .value.forEach((id) => {
          const index = this.all.value.findIndex((b) => b.id === id);
          if (index > -1) {
            this.removeAt(index);
          }
        });
    }

    const indexToRemove = this.activeIndex$();
    this.removeAt(indexToRemove);
    if (indexToRemove === 0) {
      // Required to trigger compute logic
      this.activeIndex$.set(-1);
      if (this.count > 0) {
        this.activeIndex$.set(0);
      }
    } else {
      let newActiveIndex = indexToRemove - 1;
      while (this.all$()[newActiveIndex].isSubBlock && newActiveIndex > 0) {
        newActiveIndex--;
      }
      this.activeIndex$.set(newActiveIndex);
    }
  }

  removeAt(blockIndex: number): void {
    this.all.removeAt(blockIndex);
    this.refreshLists();
  }

  removeChildBlock(removeIndex: number, blockToRemoveId: string): void {
    const indexOfBlockToRemove = this.all$().findIndex((b) => b.id === blockToRemoveId);

    if (removeIndex < 0 || indexOfBlockToRemove < 0) {
      return;
    }

    (this.active$().get('groupedBlockIds') as FormArray).removeAt(removeIndex);

    this.amendSubBlocks(this.active$());

    this.emitBlockChange(this.active$().value as BlockModel);
    this.removeAt(indexOfBlockToRemove);
    this.refreshLists();
  }

  removeLastQuestionFromBlock(blockId: string): void {
    this.removeQuestionFromBlock(blockId, 'last');
  }

  removeQuestionFromBlock(blockId: string, removeIndex: number | 'last'): void {
    const block = this.all.controls.find((c) => c.value.id === blockId);

    removeIndex = removeIndex === 'last' ? block.value.groupedQuestionIds.length - 1 : removeIndex;

    (block.get('groupedQuestionIds') as FormArray).removeAt(removeIndex);
    this.removeStaleStimulusBindings(block);

    this.emitBlockChange(block.value as BlockModel);
    this.refreshLists();
  }

  removeBlocksAffectedBy(questionId: string): void {
    this.removeQuestionFromBlocks(questionId);
    this.removeBySourceQId(questionId);
  }

  addConditionToActiveBlock(addCondition: AddCondition): void {
    const conditionToAdd = new ConditionForm(addCondition.condition).form;
    if (this.active$().value.visibleWhen.length > 0) {
      this.activeBlockConditions.push(new FormControl(addCondition.command));
    }
    this.activeBlockConditions.push(conditionToAdd);
  }

  affectedConditions(questionId: string): string[] {
    const blocksUsingQuestionForConditions = this.all.value.filter((q) =>
      q.visibleWhen.filter((vw) => typeof vw === 'object').some((vw: Condition) => vw.questionId === questionId)
    );

    return blocksUsingQuestionForConditions.map((q) => q.id);
  }

  affectedBlocksByIncompatibleCondition(questionId: string, allowedOperators: Operator[]): BlockModel[] {
    const isIncompatible = (vw: Condition): boolean => vw.questionId === questionId && !allowedOperators.includes(vw.operator);

    return this.all.value.filter((b) => b.visibleWhen.filter((vw) => typeof vw === 'object').some(isIncompatible)) as BlockModel[];
  }

  refreshLists(): void {
    this.all$.set(this.map());
    this.refreshed$.next();
  }

  refreshBlockTitle(blockId: string): void {
    const blockTitle = this.all.controls.find((control) => control.value.id === blockId)?.value.title;

    this.all$.update((blocks) => {
      const block = blocks.find((b) => b.id === blockId);

      if (block) {
        block.title = blockTitle;
      }

      return [...blocks];
    });
  }

  patch(blocks: BlockModel[]): void {
    if (!blocks) {
      return;
    }

    blocks.forEach((block) => {
      const blockForm = new BlockForm(block.id, block.type, block.isSubBlock);
      blockForm.patch(block);

      this.all.push(blockForm.form);
    });

    this.all$.set(blocks);
  }

  map(): BlockModel[] {
    return this.all.value.map((value) => {
      const block = plainToClass(BlockModel, value);
      if (block.type === BlockType.Loop) {
        if (block.sourceType !== BlockLoopSourceEnum.Question) {
          delete block.sourceQId;
        }
        if (block.sourceType !== BlockLoopSourceEnum.Options) {
          delete block.sourceOptions;
        }
        if (block.sourceType !== BlockLoopSourceEnum.Media) {
          delete block.sourceMedia;
          delete block.stimulusQuestionIds;
        }
      }
      return block;
    });
  }

  setSourceQuestion(blockId: string, questionId: string): void {
    const block = this.all.controls.find((c) => c.value.id === blockId);
    block.get('sourceQId').setValue(questionId);
    this.emitBlockChange(block.value as BlockModel);
    this.refreshLists();
  }

  setSourceType(blockId: string, source: BlockLoopSourceEnum): void {
    const block = this.all.controls.find((c) => c.value.id === blockId);
    const previousSource = block.controls.sourceType.value;
    if (previousSource === source) {
      return;
    }
    block.controls.sourceType.setValue(source);
    if (source === BlockLoopSourceEnum.Media && block.controls.sourceMedia.value === undefined) {
      block.controls.sourceMedia.setValue([]);
      block.controls.stimulusQuestionIds.setValue([]);
    }
    if (source === BlockLoopSourceEnum.Options && block.controls.sourceOptions.length === 0) {
      const { id } = reserveOptionId(block.controls.lastOptionIndex, block.controls.sourceOptions.getRawValue());
      block.controls.sourceOptions.push(new FormGroup({ id: new FormControl(id), text: new FormControl('') }));
    }
    this.emitBlockChange(block.value as BlockModel);
    this.refreshLists();
  }

  setStimulusQuestion(blockId: string, questionId: string, selected: boolean): void {
    const block = this.all.controls.find((control) => control.value.id === blockId);
    if (!block || block.value.sourceType !== BlockLoopSourceEnum.Media || !block.value.groupedQuestionIds.includes(questionId)) {
      return;
    }
    const ids = new Set(block.controls.stimulusQuestionIds.value ?? []);
    if (selected) {
      ids.add(questionId);
    } else {
      ids.delete(questionId);
    }
    block.controls.stimulusQuestionIds.setValue([...ids]);
    this.refreshLists();
  }

  resetSourceQuestion(blockId: string): void {
    const block = this.all.controls.find((c) => c.value.id === blockId);
    block.get('sourceQId').reset();
    (block.get('groupedQuestionIds') as FormArray).clear();
    this.removeStaleStimulusBindings(block);
    this.emitBlockChange(block.value as BlockModel);
    this.refreshLists();
  }

  swapPosition(direction: 'up' | 'down', blockId: string, questionId: string): void {
    const block = this.all.controls.find((c) => c.value.id === blockId);

    const groupPosition = block.value.groupedQuestionIds.findIndex((qId) => qId === questionId);
    const newPosition = direction === 'up' ? groupPosition - 1 : groupPosition + 1;
    const tempQId = block.value.groupedQuestionIds[newPosition];
    (block.get('groupedQuestionIds') as FormArray).at(newPosition).setValue(questionId);
    (block.get('groupedQuestionIds') as FormArray).at(groupPosition).setValue(tempQId);

    this.emitBlockChange(block.value as BlockModel);
    this.refreshLists();
  }

  swapPositionById(movingQuestionId: string, targetQuestionId: string, blockId: string): void {
    const block = this.all.controls.find((c) => c.value.id === blockId);

    const groupPosition = block.value.groupedQuestionIds.findIndex((qId) => qId === movingQuestionId);
    let newPosition = block.value.groupedQuestionIds.findIndex((qId) => qId === targetQuestionId);

    if (groupPosition < newPosition) {
      newPosition--;
    }

    (block.get('groupedQuestionIds') as FormArray).removeAt(groupPosition);
    (block.get('groupedQuestionIds') as FormArray).insert(newPosition, new FormControl(movingQuestionId));

    this.emitBlockChange(block.value as BlockModel);
    this.refreshLists();
  }

  moveQuestionToEdge(blockId: string, questionId: string, edge: 'start' | 'end'): void {
    const block = this.all.controls.find((c) => c.value.id === blockId);
    const groupedControl = block.get('groupedQuestionIds') as FormArray;
    const currentIndex = block.value.groupedQuestionIds.findIndex((qId) => qId === questionId);

    if (currentIndex === -1) {
      return;
    }

    groupedControl.removeAt(currentIndex);

    if (edge === 'start') {
      groupedControl.insert(0, new FormControl(questionId));
    } else {
      groupedControl.push(new FormControl(questionId));
    }

    this.emitBlockChange(block.value as BlockModel);
    this.refreshLists();
  }

  amendBlocks(): void {
    const emptyBlocks = this.all.controls
      .map((block) => block.value)
      .filter((block) => !BlockTypeContainers.includes(block.type) && block.groupedQuestionIds.length === 0)
      .map((block) => block.id);

    emptyBlocks.forEach((blockId) => {
      this.removeSubBlock(blockId);

      const blockIndex = this.all.value.findIndex((b) => b.id === blockId);
      if (blockIndex > -1) {
        this.removeAt(blockIndex);
      }
    });
  }

  private removeStaleStimulusBindings(block: FormGroup<IBlockForm>): void {
    const ids = block.controls.stimulusQuestionIds.value;
    if (ids) {
      block.controls.stimulusQuestionIds.setValue(ids.filter((id) => block.value.groupedQuestionIds.includes(id)));
    }
  }

  private removeBySourceQId(questionId: string): void {
    const blockIndex = this.all.value.findIndex((b) => b.sourceQId === questionId);
    if (blockIndex === -1) {
      return;
    }

    const block = this.all$()[blockIndex];
    if (block.isSubBlock) {
      this.removeSubBlock(block.id);
    }

    this.removeAt(blockIndex);
  }

  private removeSubBlock(blockId: string): void {
    const parentBlock = this.byGroupedBlocksId(blockId);
    if (!parentBlock) {
      return;
    }
    const childIndex = parentBlock.groupedBlockIds.findIndex((bId) => bId === blockId);
    const parentBlockCtrl = this.all.controls.find((c) => c.value.id === parentBlock.id);

    if (parentBlockCtrl && childIndex > -1) {
      (parentBlockCtrl.get('groupedBlockIds') as FormArray).removeAt(childIndex);
      this.amendSubBlocks(parentBlockCtrl);
    }

    this.emitBlockChange(parentBlockCtrl.value as BlockModel);
  }

  private amendSubBlocks(parentBlock: FormGroup<IBlockForm>): void {
    parentBlock.get('groupedBlockIds').value.forEach((blockId, index) => {
      const childBlock = this.all.controls.find((b) => b.value.id === blockId);
      if (childBlock) {
        childBlock.get('id').setValue(`${parentBlock.value.id.replace('B', 'SB')}.${index + 1}`);
        (parentBlock.get('groupedBlockIds') as FormArray).at(index).setValue(childBlock.value.id);
      }
    });
  }

  private removeQuestionFromBlocks(questionId: string): void {
    const blockCtrl = this.all.controls.find((c) => c.value.id === this.byGroupedQId(questionId)?.id);
    if (!blockCtrl) {
      return;
    }

    const questionIndex = blockCtrl.value.groupedQuestionIds.indexOf(questionId);
    if (questionIndex > -1) {
      (blockCtrl.get('groupedQuestionIds') as FormArray).removeAt(questionIndex);
      this.removeStaleStimulusBindings(blockCtrl);

      if (blockCtrl.value.groupedQuestionIds.length === 0 && !BlockTypeContainers.includes(blockCtrl.value.type)) {
        this.removeAt(this.all.controls.findIndex((c) => c.value.id === blockCtrl.value.id));
        this.removeSubBlock(blockCtrl.value.id);
        this.refreshLists();
        return;
      }
      this.emitBlockChange(blockCtrl.value as BlockModel);
    }

    this.amendBlocks();
  }

  private listenToTitleChange(): void {
    this.all.controls[this.activeIndex$()]
      .get('title')
      .valueChanges.pipe(debounceTime(1000))
      .subscribe((blockTitle) => {
        this.all$.update((blocks) => {
          blocks[this.activeIndex$()].title = blockTitle;
          return [...blocks];
        });
      });
  }

  private listenToEvenDistribution(): void {
    this.all.controls[this.activeIndex$()].get('evenDistributionInd').valueChanges.subscribe((evenDistribution) => {
      if (evenDistribution) {
        const quotas = this.all.controls[this.activeIndex$()].get('quotas').value;
        const groupedBlockIds = this.all.controls[this.activeIndex$()].get('groupedBlockIds').value;

        for (const id of groupedBlockIds) {
          if (quotas.find((q) => q.id === id)) {
            continue;
          } else {
            this.addQuotaFormGroup(id);
          }
        }
      } else {
        (this.all.controls[this.activeIndex$()].get('quotas') as FormArray).clear();
      }
    });
  }

  private listenToSubBlocks(): void {
    this.all.controls[this.activeIndex$()].get('groupedBlockIds').valueChanges.subscribe((groupedBlockIds) => {
      const quotas = this.all.controls[this.activeIndex$()].get('quotas').value;
      const evenDistribution = this.all.controls[this.activeIndex$()].get('evenDistributionInd').value;

      if (!evenDistribution) {
        return;
      }

      for (const id of groupedBlockIds) {
        if (quotas.find((q) => q.id === id)) {
          continue;
        } else {
          this.addQuotaFormGroup(id);
        }
      }

      for (const quota of quotas) {
        if (groupedBlockIds.includes(quota.id)) {
          continue;
        } else {
          const index = (this.all.controls[this.activeIndex$()].get('quotas') as FormArray).controls.findIndex((group) => group.get('id')?.value === quota.id);

          if (index !== -1) {
            (this.all.controls[this.activeIndex$()].get('quotas') as FormArray).removeAt(index);
          }
        }
      }
    });
  }

  private addQuotaFormGroup(id: string): void {
    const quotaControl = new FormControl<number>(1, [Validators.min(1)]);

    const subscription = quotaControl.valueChanges.subscribe((value) => {
      if (value < 1) {
        quotaControl.setValue(1, { emitEvent: false });
      }
    });

    this.quotaSubscriptions.push(subscription);

    (this.all.controls[this.activeIndex$()].get('quotas') as FormArray).push(
      new FormGroup({
        id: new FormControl<string>(id),
        quota: quotaControl
      })
    );
  }

  private emitBlockChange(block: BlockModel): void {
    if (block.isSubBlock) {
      const parentBlock = this.byGroupedBlocksId(block.id);
      if (parentBlock) {
        this.change$.next({ blocks: parentBlock.groupedBlockIds.map((bId) => this.byId(bId)), parentBlock: parentBlock });
      }
    } else if (BlockTypeContainers.includes(block.type)) {
      this.change$.next({ blocks: block.groupedBlockIds.map((bId) => this.byId(bId)), parentBlock: block });
    } else {
      this.change$.next({ blocks: [block] });
    }
  }
}
